import * as THREE from 'three';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

// God rays: radial scatter march toward the projected sun position, masked by
// scene luminance — the signature sunset-over-water shot.
export function createGodRaysPass(): ShaderPass {
  return new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uSunScreenPos: { value: new THREE.Vector2(0.5, 0.7) },
      uIntensity: { value: 0.42 },
      uDecay: { value: 0.88 },
      uDensity: { value: 0.42 },
      uWeight: { value: 0.14 },
      uClamp: { value: 1.0 },
    },
    vertexShader: /* glsl */ `
varying vec2 vUv;
void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`,
    fragmentShader: /* glsl */ `
uniform sampler2D tDiffuse;
uniform vec2 uSunScreenPos;
uniform float uIntensity;
uniform float uDecay;
uniform float uDensity;
uniform float uWeight;
uniform float uClamp;
varying vec2 vUv;
void main(){
  // Fade rays as the sun leaves the frame: off-screen sun must not smear edges.
  float sunDist = length((uSunScreenPos - vec2(0.5)) * vec2(2.0, 2.0));
  float onScreen = 1.0 - smoothstep(0.55, 1.1, sunDist);
  if (onScreen < 0.004) {
    gl_FragColor = vec4(texture2D(tDiffuse, vUv).rgb, 1.0);
    return;
  }
  vec2 dir = (uSunScreenPos - vUv) * uDensity;
  // Corridor mask: rays only along the sun azimuth, like the glitter path —
  // no full-frame scatter when the band marches across off-corridor pixels.
  float corridor = smoothstep(0.86, 0.99, dot(normalize(uSunScreenPos - vec2(0.5) + 1e-5), normalize(vUv - uSunScreenPos + 1e-5)));
  dir *= 1.0 / 48.0; // 48 samples
  vec2 uv = vUv;
  vec3 acc = vec3(0.0);
  float decay = 1.0;
  for (int i = 0; i < 48; i++) {
    uv += dir;
    vec3 s = texture2D(tDiffuse, clamp(uv, 0.0, 1.0)).rgb;
    float lum = dot(s, vec3(0.2126, 0.7152, 0.0722));
    // only bright pixels scatter (sun, glitter, beacons)
    s *= smoothstep(0.9, 1.4, lum) * uClamp;
    acc += s * decay * uWeight;
    decay *= uDecay;
  }
  vec3 base = texture2D(tDiffuse, vUv).rgb;
  gl_FragColor = vec4(base + clamp(acc, 0.0, 1.6) * uIntensity * onScreen * (0.25 + 0.75 * corridor), 1.0);
}
`,
  });
}

// Final grade: warm highlights / teal shadows, chromatic aberration toward edges,
// film grain, vignette, and subtle speed streaks at high uSpeed.
export function createGradePass(): ShaderPass {
  return new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uStrength: { value: 0.5 },
      uSize: { value: 0.8 },
      uAberration: { value: 0.0016 },
      uGrain: { value: 0.05 },
      uSpeed: { value: 0 },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
varying vec2 vUv;
void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`,
    fragmentShader: /* glsl */ `
uniform sampler2D tDiffuse;
uniform float uStrength;   // vignette strength
uniform float uSize;       // vignette size
uniform float uAberration; // chromatic aberration amount
uniform float uGrain;
uniform float uSpeed;
uniform float uTime;
varying vec2 vUv;

float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }

void main(){
  vec2 fromCenter = vUv - vec2(0.5);
  float r = length(fromCenter);

  // Chromatic aberration: sample RGB with slightly different radial offsets
  vec2 caOffset = fromCenter * uAberration * (0.4 + r);
  vec3 col;
  col.r = texture2D(tDiffuse, vUv + caOffset).r;
  col.g = texture2D(tDiffuse, vUv).g;
  col.b = texture2D(tDiffuse, vUv - caOffset).b;

  // Cinematic grade: warm highlights, teal shadows
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  vec3 warm = vec3(1.06, 0.99, 0.9);
  vec3 teal = vec3(0.94, 1.0, 1.06);
  col *= mix(teal, warm, smoothstep(0.15, 0.85, lum));

  // Gentle S-curve contrast
  col = col * col * (3.0 - 2.0 * col) * 0.35 + col * 0.65;

  // Speed streaks: radial smears toward edges at speed
  if (uSpeed > 0.05) {
    vec2 streakDir = normalize(fromCenter + 1e-5);
    float streak = texture2D(tDiffuse, vUv - streakDir * r * 0.012 * uSpeed).g;
    col.g = mix(col.g, streak, uSpeed * 0.22 * smoothstep(0.25, 0.75, r));
  }

  // Film grain
  float g = hash(vUv * 913.0 + fract(uTime) * 57.0);
  col += (g - 0.5) * uGrain;

  // Vignette
  float vig = smoothstep(uSize, uSize - 0.5, r);
  col *= mix(1.0, vig, uStrength);

  gl_FragColor = vec4(col, 1.0);
}
`,
  });
}

// Project a world-space sun position to screen UV for the god-rays center.
export function projectSunToScreen(
  camera: THREE.PerspectiveCamera,
  sunWorldPos: THREE.Vector3,
  out: THREE.Vector2,
): void {
  const ndc = sunWorldPos.clone().project(camera);
  out.set(ndc.x * 0.5 + 0.5, ndc.y * 0.5 + 0.5);
}
